"use no memo";

import type { CellContext, Column, ColumnDef } from "@tanstack/react-table";
import { LuEllipsis as LuMoreHorizontal, LuLock } from "react-icons/lu";

import { isAccountCoinAccount } from "@/app/(app)/[userId]/utils";
import CoinPriceFormatter from "@/components/shared/coin-price-formatter";
import { FollowButton } from "@/components/shared/follow-button";
import { TriggerLoggedInModalWindow } from "@/components/shared/is-logged-in";
import { Button } from "@/components/ui/button";
import {
  DropdownDrawer,
  DropdownDrawerContent,
  DropdownDrawerItem,
  DropdownDrawerTrigger,
} from "@/components/ui/dropdown-drawer";
import { Progress } from "@/components/ui/progress";
import type { TokenBalanceSummary } from "@/graphql/codegen/graphql";
import type { CoinPropertiesResponse } from "@/hooks/use-coin-properties-query";
import { FOCUS_TOKEN_DISPLAY_NAME } from "@/utils/constants";
import {
  formatDecimalValue,
  getVisiblePrice,
  tokenNanosToInteger,
  usdToFocus,
} from "@/utils/currency";
import { getUserName } from "@/utils/usernames";

import { MessageLink } from "../messages/message-link";
import { renderSortingHeader } from "../trade/table-sorting-headers";
import { renderCoinUserTableCell } from "../trade/trending-coins/trending-coins-cell-content";

interface ColumnProps {
  holdersPercentageMap: Record<string, number>;
  currentUserPublicKey: string;
  hasCoinPriceInQuoteCurrency: boolean;
  focusExchangeRate: number | null;
  coinPropertiesData: CoinPropertiesResponse | null | undefined;
  isLoggedIn: boolean;
}

interface CustomCellContext extends CellContext<TokenBalanceSummary, unknown> {
  refetchData?: () => void;
}

export const tableColumns = ({
  holdersPercentageMap,
  currentUserPublicKey,
  hasCoinPriceInQuoteCurrency,
  focusExchangeRate,
  coinPropertiesData,
  isLoggedIn,
}: ColumnProps): ColumnDef<TokenBalanceSummary>[] => {
  return [
    {
      accessorKey: "id",
      header: () => (
        <span className="flex items-center text-xs text-muted">#</span>
      ),
      cell: ({ row }: CustomCellContext) => {
        const { id } = row;
        return <span className="text-xs text-muted">{+id + 1}</span>;
      },
    },
    {
      accessorKey: "hodler.publicKey",
      header: () => <span className="flex items-center text-xxs">Holder</span>,
      cell: ({ row }: CustomCellContext) => {
        return renderCoinUserTableCell({
          coinUsername: row.original.hodler?.username ?? "",
          coinPublicKey: row.original.hodler?.publicKey ?? "",
          extraData: row.original.hodler?.extraData ?? {},
          clickable: true,
          usernamePrefix: isAccountCoinAccount(row.original.hodler) ? "$" : "@",
          footerText: row.original.hodler
            ? getUserName({ account: row.original.hodler })
            : "",
          userNameClassName: "w-[130px] sm:w-auto lg:w-[130px] xl:w-[170px]",
        });
      },
    },
    {
      accessorKey: "totalBalance",
      header: ({ column }: { column: Column<TokenBalanceSummary, unknown> }) =>
        renderSortingHeader({
          column,
          title: "Coins Held",
          className: "text-xxs",
        }),
      cell: ({ row }: CustomCellContext) => {
        const lockedBalanceNanos = Number(
          row.original.lockedBalanceNanos ?? "0",
        );
        return (
          <div className="flex flex-col gap-1 text-left text-sm">
            <CoinPriceFormatter
              price={tokenNanosToInteger(row.original?.totalBalance || 0)}
              isUsd={false}
              highPriceThreshold={10}
              className="font-mono text-sm text-muted-foreground"
            />
            {lockedBalanceNanos > 0 && (
              <div className="flex items-center gap-1 text-muted">
                <LuLock />
                <CoinPriceFormatter
                  price={tokenNanosToInteger(lockedBalanceNanos)}
                  className="font-mono"
                  isUsd={false}
                />
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "ownership",
      header: () => (
        <span className="flex items-center text-xxs">Ownership</span>
      ),
      cell: ({ row }: CustomCellContext) => {
        const percentage =
          holdersPercentageMap[row.original.hodler?.publicKey as string];
        const progressValue = Math.max(percentage, 1);

        return (
          <div className="relative min-w-24 py-3">
            <div
              className="
              mb-1 text-left font-mono text-sm text-muted-foreground
            "
            >
              {formatDecimalValue(percentage)}%
            </div>
            <Progress
              value={progressValue}
              className="h-1 w-full bg-card"
              indicatorClassName="bg-muted-foreground"
            />
          </div>
        );
      },
    },
    ...(hasCoinPriceInQuoteCurrency
      ? [
          {
            accessorKey: "marketValue",
            header: () => (
              <span className="flex items-center text-xxs">Market Value</span>
            ),
            cell: ({ row }: CustomCellContext) => {
              if (!focusExchangeRate || !coinPropertiesData?.coinPrice) {
                return null;
              }
              const tokenPriceUsd = getVisiblePrice(
                coinPropertiesData.coinPrice,
              );

              const marketValueUsd =
                tokenNanosToInteger(row.original?.totalBalance || 0) *
                tokenPriceUsd;

              return (
                <div className="flex flex-col gap-0 text-left">
                  <div
                    className="
                      flex items-center font-mono text-muted-foreground
                    "
                  >
                    <span className="font-mono text-xs text-emerald-400">
                      ~
                      <CoinPriceFormatter price={marketValueUsd} />
                    </span>
                  </div>
                  <div className="font-mono text-muted">
                    <span className="inline-block text-xs">
                      <CoinPriceFormatter
                        isUsd={false}
                        price={usdToFocus(marketValueUsd, focusExchangeRate)}
                      />{" "}
                      <span className="text-[10px]">
                        {FOCUS_TOKEN_DISPLAY_NAME}
                      </span>
                    </span>
                  </div>
                </div>
              );
            },
          },
        ]
      : []),
    {
      id: "actions",
      cell: ({ row }: CustomCellContext) => {
        const holderPublicKey = row.original.hodler?.publicKey;

        if (!holderPublicKey || holderPublicKey === currentUserPublicKey)
          return (
            <Button
              variant="ghost"
              size="icon"
              className="
                absolute top-px left-0 size-full rounded-none border-b bg-card
                text-sm
                hover:bg-card
              "
            />
          );

        return (
          <DropdownDrawer
            historyName="holders-row-actions"
            title="Holder actions"
          >
            <DropdownDrawerTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon"
                  className="
                    absolute top-px left-0 size-full rounded-none border-b
                    bg-card text-sm
                    hover:bg-card
                  "
                >
                  <LuMoreHorizontal className="size-4" />
                </Button>
              }
            />
            <DropdownDrawerContent align="end" sideOffset={-8}>
              {isLoggedIn && (
                <DropdownDrawerItem
                  render={
                    <div>
                      <FollowButton
                        variant="link"
                        wrapperClassName="w-full"
                        className="
                          h-auto w-full justify-start rounded-none border-0 p-0
                          text-left text-sm font-medium text-foreground
                          hover:bg-transparent hover:text-foreground
                        "
                        {...{
                          publicKey: holderPublicKey,
                        }}
                      />
                    </div>
                  }
                />
              )}

              <TriggerLoggedInModalWindow>
                <DropdownDrawerItem
                  render={
                    <MessageLink
                      publicKey={holderPublicKey}
                      buttonVariant="link"
                      className="text-sm"
                      buttonClassName="h-auto w-full justify-start text-foreground no-underline rounded-none border-0 p-0 text-left text-sm hover:bg-transparent hover:no-underline"
                      isTooltip={false}
                      content="Send Message"
                    />
                  }
                />
              </TriggerLoggedInModalWindow>
            </DropdownDrawerContent>
          </DropdownDrawer>
        );
      },
    },
  ];
};
